import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import * as _ from 'lodash';
import { ApplicationContext } from '../models/application-context.model';
import { PortalConfig } from '../models/borrower-portal/portal-config.model';
import { PortalContent } from '../models/borrower-portal/portal-content-model-model';
import { UserData } from '../models/user';
import { UserService } from './user.service';
import { CountryEnumerationItem, EnumsService } from './enums.service';
import { PortalServiceFactory } from './portal-service.factory';
import { BasePortalService } from './base-portal.service';
import { LocalStorageService } from './local-storage.service';
import { Constants } from './constants';
import { PortalScope } from '../login/login.component';

@Injectable({ providedIn: 'root' })
export class ApplicationContextService {
  private _applicationContext: ApplicationContext;

  private _contextSubject: Subject<ApplicationContext> =
    new Subject<ApplicationContext>();

  private _portalService: BasePortalService;

  get context(): Observable<ApplicationContext> {
    if (this._applicationContext) {
      return of(this._applicationContext);
    }
    if (!this._portalService) {
      const authData = this._localStorageService.authorizationData;
      if (authData) {
        this._portalService = this._portalServiceFactory.createByScope(authData.scope as PortalScope);
      }
    }
    return this.initializeApplicationContext().pipe(
      map((context) => {
        this._applicationContext = context;
        return context;
      })
    );
  }

  get changes(): Subject<ApplicationContext> {
    return this._contextSubject;
  }

  constructor(
    private readonly _userService: UserService,
    private readonly _enumService: EnumsService,
    private readonly _portalServiceFactory: PortalServiceFactory,
    private readonly _localStorageService: LocalStorageService
  ) {}

  reset = () => {
    this._applicationContext = null;
    this._portalService = null;
    //this._contextSubject.next(this._applicationContext);
  };

  private initializeApplicationContext = (): Observable<ApplicationContext> => {
    return this._userService.getLoggedInUserData().pipe(
      catchError((error) => of(error)),
      switchMap((user: UserData) =>
        this._portalService.getPortalConfig().pipe(
          catchError((error) => of(error)),
          switchMap((portalConfig: PortalConfig) =>
            this._enumService.getAllCountries().pipe(
              catchError((error) => of(error)),
              switchMap((countries) => {
                const portalContentObs = this._portalService
                  ? this._portalService.getPortalContent().pipe(
                    catchError(error => {
                      console.warn('Failed to fetch portal content:', error);
                      return of(null);
                    })
                  )
                  : of(null);

                return portalContentObs.pipe(
                  map(portalContent => this.populateContextFieldsFromApiResults(
                    user,
                    portalConfig,
                    countries,
                    portalContent
                  ))
                );
              })
            )
          )
        )
      )
    );
  };

  private populateContextFieldsFromApiResults = (
    user: UserData,
    portalConfig: PortalConfig,
    countries: CountryEnumerationItem[],
    portalContent: PortalContent | null = null
  ): ApplicationContext => {
    const context = new ApplicationContext();
    context.currentlyLoggedInUser = user;

    context.globalConfig = {
      loanStatus: _.orderBy(portalConfig.loanStatuses, ['order', 'asc']),
      documentType: portalConfig.documentTypes,
      loanPurpose: portalConfig.loanPurposes,
      countries
    };
    context.userPermissions = {
      userId: portalConfig.userId,
      companyId: portalConfig.companyId
    };
    
    // Set portal content if provided
    if (portalContent) {
      context.portalContent = portalContent;
    }
    
    return context;
  };

  updateContext = (context: ApplicationContext) => {
    this._contextSubject.next(context);
  };
}
